import { DatePipe } from '@angular/common';
import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { DefaultService } from '@codegen/service';
import { AuthorizationService } from '@modules/authorization/services/authorization.service';
import {
  AuthorizationTypes,
  ChangeQuestionnaireProcessStep,
  GetProcessCode,
  GetRedirectUrl,
  Logout,
  ProceedRegistration,
  ReAuthorization,
  SetRedirectUrl,
  StartRegistrationNewUser,
  UpdateAuthorizationToken,
  UpdateAuthProcess,
} from '@modules/authorization/store/authorization.action';
import {
  SetButtons,
  SetCurrentStep,
  SetFormField,
  SetFormTitle,
} from '@modules/dynamic-form/store/dynamicForm.action';
import { DictionaryService } from '@modules/shared/services';
import { AppState } from '@modules/shared/store';
import {
  AuthorizationFinish,
  HideSpinnerLoading,
  SetSmsResponseCode,
} from '@modules/shared/store/common.actions';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { EMPTY } from 'rxjs';
import {
  concatMap,
  mergeMap,
  map,
  switchMap,
  tap,
  catchError,
} from 'rxjs/operators';

@Injectable()
export class AuthorizationEffects {
  private actions$ = inject(Actions);
  private store = inject<Store<AppState>>(Store);
  private authorizationService = inject(AuthorizationService);
  private authorizationResourceService = inject(DefaultService);
  private datePipe = inject(DatePipe);
  private dictionaryService = inject(DictionaryService);
  private router = inject(Router);

  public setFormFieldStart$ = createEffect(() =>
    this.actions$.pipe(
      ofType<StartRegistrationNewUser>(
        AuthorizationTypes.StartRegistrationNewUser
      ),
      switchMap(() => {
        return this.authorizationService
          .startRegistrationNewUser()
          .pipe(concatMap(() => [new HideSpinnerLoading()]));
      })
    )
  );

  public setFormFieldsProceed$ = createEffect(() =>
    this.actions$.pipe(
      ofType<ProceedRegistration>(AuthorizationTypes.ProceedRegistration),
      switchMap(() =>
        this.authorizationService.proceedRegistration().pipe(
          concatMap(res => {
            if (res.needConfirmation) {
              this.router.navigate(['/auth/authConfirm']);
              return [
                new SetSmsResponseCode(res.errorInfo.error),
                new SetButtons(res.allowedActions),
              ];
            } else {
              this.router.navigate(['/auth']);
              return [
                new SetFormField(
                  res.questionnaireStep.groupList[0].questionList
                ),
                new SetCurrentStep(res.questionnaireStep.hint),
                new SetButtons(res.allowedActions),
                new SetFormTitle(res.questionnaireStep.displayName),
                new HideSpinnerLoading(),
              ];
            }
          })
        )
      )
    )
  );

  public authCheck$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthorizationTypes.AuthorizationCheck),
      switchMap(() => {
        return this.authorizationResourceService
          .authCheck({ confirmationData: '' })
          .pipe(
            switchMap(response => {
              if (response.preAuthState === 'UNKNOWN') {
                return [new GetRedirectUrl()];
              }

              if (response.preAuthState === 'REGISTRATION_NEED_QUESTIONNAIRE') {
                return [new UpdateAuthProcess(response.questionnaireId)];
              }

              return [];
            })
          );
      })
    )
  );

  public updateAuthorizationToken$ = createEffect(() =>
    this.actions$.pipe(
      ofType<UpdateAuthorizationToken>(
        AuthorizationTypes.UpdateAuthorizationToken
      ),
      switchMap(() =>
        this.authorizationResourceService.authRefresh().pipe(
          concatMap((): Action[] => {
            return [];
          })
        )
      )
    )
  );

  public ChangeQuestionnaireProcessStep$ = createEffect(() =>
    this.actions$.pipe(
      ofType<ChangeQuestionnaireProcessStep>(
        AuthorizationTypes.ChangeQuestionnaireProcessStep
      ),
      map(data => data.payload),
      switchMap(data => {
        const { stepType, formValue } = data;
        return this.authorizationService
          .changeQuestionnaireProcessStep(
            localStorage.getItem('processId'),
            stepType,
            formValue
          )
          .pipe(
            switchMap(res => {
              if (res.needConfirmation) {
                this.router.navigate(['/auth/authConfirm']);
                return [
                  new SetSmsResponseCode(res.errorInfo.error),
                  new SetButtons(res.allowedActions),
                  new HideSpinnerLoading(),
                ];
              } else if (
                res.errorInfo.error === '11003' ||
                res.errorInfo.error === '11000'
              ) {
                return [new UpdateAuthProcess(res.questionnaireId)];
              } else {
                this.router.navigate(['/auth']);
                return [
                  new SetFormField(
                    res.questionnaireStep.groupList[0].questionList
                  ),
                  new SetCurrentStep(res.questionnaireStep.hint),
                  new SetButtons(res.allowedActions),
                  new SetFormTitle(res.questionnaireStep.displayName),
                  new HideSpinnerLoading(),
                ];
              }
            })
          );
      })
    )
  );

  public UpdateAuthProcess$ = createEffect(() =>
    this.actions$.pipe(
      ofType<UpdateAuthProcess>(AuthorizationTypes.UpdateProcess),
      map(data => data.payload),
      switchMap(questionnaireId => {
        return this.authorizationService.updateAuthProcess(questionnaireId);
      }),
      switchMap(res => {
        return [
          new SetFormField(res.questionnaireStep.groupList[0].questionList),
          new SetCurrentStep(res.questionnaireStep.hint),
          new SetButtons(res.allowedActions),
          new SetFormTitle(res.questionnaireStep.displayName),
          new HideSpinnerLoading(),
        ];
      })
    )
  );

  public Logout$ = createEffect(() =>
    this.actions$.pipe(
      ofType<Logout>(AuthorizationTypes.Logout),
      mergeMap(() => {
        return this.authorizationService.logoutCurrentUser().pipe(
          tap(() => {
            this.authorizationService.removeCookie('accessToken');
            this.authorizationService.removeCookie('refreshToken');
            localStorage.removeItem('code');
            localStorage.removeItem('authorized');
            localStorage.removeItem('authState');
            localStorage.removeItem('processId');
            localStorage.removeItem('order');
            localStorage.removeItem('route');
            this.router.navigateByUrl('/');
          }),
          map(() => new StartRegistrationNewUser()),
          catchError(err => {
            console.error('logout error', err);
            return EMPTY;
          })
        );
      })
    )
  );

  public GetRedirectUrl$ = createEffect(() =>
    this.actions$.pipe(
      ofType<GetRedirectUrl>(AuthorizationTypes.GetRedirectUrl),
      switchMap(() => {
        return this.authorizationResourceService.authOAuthRedirectUrl();
      }),
      switchMap(res => {
        localStorage.setItem('authState', res.state);
        return [new SetRedirectUrl(res.redirectUrl)];
      })
    )
  );

  public GetProcessCode$ = createEffect(() =>
    this.actions$.pipe(
      ofType<GetProcessCode>(AuthorizationTypes.GetProcessCode),
      switchMap(() => {
        return this.authorizationService.processCode();
      }),
      switchMap(res => {
        if (res.errorInfo.error === '11003') {
          localStorage.removeItem('processId');
          this.router.navigate(['']);
          return [new StartRegistrationNewUser()];
        }
        if (res.errorInfo.error === '10100') {
          localStorage.removeItem('processId');
          this.router.navigate(['']);
          localStorage.setItem('authorized', 'true');
          this.store.dispatch(new AuthorizationFinish());
          return [];
        } else if (res.needConfirmation) {
          this.router.navigate(['/auth/authConfirm']);
          return [
            new SetSmsResponseCode(res.errorInfo.error),
            new SetButtons(res.allowedActions),
          ];
        }
        this.router.navigate(['/auth']);
        return [
          new SetFormField(res.questionnaireStep.groupList[0].questionList),
          new SetCurrentStep(res.questionnaireStep.hint),
          new SetButtons(res.allowedActions),
          new SetFormTitle(res.questionnaireStep.displayName),
          new HideSpinnerLoading(),
        ];
      })
    )
  );

  public ReAuthorization$ = createEffect(() =>
    this.actions$.pipe(
      ofType<ReAuthorization>(AuthorizationTypes.ReAuthorization),
      switchMap(() => {
        return this.authorizationService.startRegistrationNewUser();
      }),
      switchMap(() => {
        return this.authorizationResourceService.authOAuthRedirectUrl();
      }),
      switchMap((res): Action[] => {
        localStorage.setItem('authState', res.state);
        window.location.href = res.redirectUrl;
        return [];
      })
    )
  );
}
